/**
 * SpotADeal — "Walk in, snap a deal, publish, earn points."
 *
 * Route: /food-pulse/spot
 *
 * Flow:
 *   1. Ask for location → suggest nearby food_businesses (GPS + haversine)
 *   2. User picks a vendor OR types a new store name/address/phone
 *   3. Product step: search master food_items catalog (educates the user)
 *      OR upload a photo of the label
 *   4. Deal specifics: original price, deal price / discount, qty, force-expiry
 *   5. Submit → points awarded, deal published to /food-pulse/deals
 *
 * All the heavy lifting lives on the backend at POST /api/deals/community.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Camera, MapPin, Search, ArrowRight, ArrowLeft, Check, Loader2, X,
  Store, Utensils, Sparkles, Award, ShieldCheck,
} from "lucide-react";
import { http, API as APIBASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";
import TranslatableInput from "@/components/TranslatableInput";

const API = process.env.REACT_APP_BACKEND_URL;
const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

// ------------------------------------------------------------------
// Step 1 — Vendor picker (GPS-first)
// ------------------------------------------------------------------
function VendorStep({ vendor, setVendor, onNext }) {
  const [coords, setCoords] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [nearby, setNearby] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [manual, setManual] = useState(false);
  const [manualForm, setManualForm] = useState({ name: "", address: "", phone: "" });

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) return setGpsError("Your browser doesn't support geolocation");
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setLoading(false);
      },
      (err) => { setGpsError(err.message || "Couldn't get your location"); setLoading(false); },
      { timeout: 8000, maximumAge: 60000 },
    );
  }, []);

  useEffect(() => { requestLocation(); }, [requestLocation]);

  useEffect(() => {
    if (!coords) return;
    setLoading(true);
    axios.get(`${API}/api/food-businesses/near`, {
      params: { lat: coords.lat, lon: coords.lon, radius_km: 2, limit: 20 },
    }).then((r) => setNearby(r.data.items || []))
      .catch(() => setNearby([]))
      .finally(() => setLoading(false));
  }, [coords]);

  const filtered = q
    ? nearby.filter((v) => v.name.toLowerCase().includes(q.toLowerCase()))
    : nearby;

  return (
    <div data-testid="scout-step-vendor">
      <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: AMBER }}>Step 1 of 3</div>
      <h2 className="font-display text-3xl sm:text-4xl mb-2">Which store are you at?</h2>
      <p className="opacity-70 text-sm mb-6">We'll auto-suggest nearby vendors. If your store isn't listed, add it in seconds.</p>

      {!coords && (
        <div className="rounded-2xl border p-5 mb-4 text-sm" style={{ borderColor: "#E9D8B4", background: "#FFF7E4" }}>
          <div className="flex items-center gap-2 mb-2"><MapPin className="w-4 h-4" style={{ color: AMBER }} /> <strong>Location helps a lot</strong></div>
          <p className="opacity-80">We use your location to find the store you're in. Nothing is stored beyond this deal.</p>
          <button onClick={requestLocation} className="mt-3 rounded-full px-4 py-2 text-white text-sm font-semibold" style={{ background: GREEN }}
                  data-testid="scout-request-location">
            Use my location
          </button>
          {gpsError && <div className="mt-2 text-red-700 text-xs">{gpsError}</div>}
        </div>
      )}

      {coords && (
        <>
          <div className="flex items-center gap-2 rounded-full border px-4 py-2 mb-4" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
            <Search className="w-4 h-4 opacity-50" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter nearby vendors…"
                   className="flex-1 outline-none text-sm bg-transparent" data-testid="scout-vendor-search" />
            {loading && <Loader2 className="w-4 h-4 animate-spin opacity-40" />}
          </div>

          <div className="grid gap-2 mb-4 max-h-80 overflow-y-auto">
            {filtered.map((v) => (
              <button key={v.id} onClick={() => setVendor({ vendor_id: v.id, vendor_slug: v.slug, vendor_name: v.name, city: v.city, state: v.state_code, gps: coords })}
                      className={`text-left rounded-xl border p-3 hover:border-emerald-800 transition-all ${vendor?.vendor_id === v.id ? "ring-2 ring-emerald-800" : ""}`}
                      style={{ borderColor: vendor?.vendor_id === v.id ? GREEN : "#E9D8B4", background: "#fff" }}
                      data-testid={`scout-vendor-${v.slug}`}>
                <div className="font-semibold">{v.name}</div>
                <div className="text-xs opacity-70 flex items-center gap-2">
                  <MapPin className="w-3 h-3" />
                  {v.city}{v.state_code ? `, ${v.state_code}` : ""} · {v.distance_km} km
                  {v.verified && <span className="rounded-full px-1.5 py-0.5 ml-1 text-[10px]" style={{ background: "#F0F5F1", color: GREEN }}>verified</span>}
                </div>
              </button>
            ))}
            {filtered.length === 0 && !loading && (
              <div className="text-sm opacity-60 py-8 text-center">
                {q ? "No match — try a different filter or add the store." : "No nearby vendors found. Add yours below."}
              </div>
            )}
          </div>

          {/* Manual add */}
          <button onClick={() => setManual(!manual)} className="text-sm underline underline-offset-4 opacity-80" data-testid="scout-vendor-add-toggle">
            {manual ? "— Cancel add" : "+ My store isn't listed"}
          </button>
          {manual && (
            <div className="rounded-2xl border p-4 mt-3" style={{ borderColor: "#E9D8B4", background: "#fff" }} data-testid="scout-vendor-manual">
              <input placeholder="Store name *" value={manualForm.name}
                     onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })}
                     className="w-full rounded-lg border px-3 py-2 mb-2 text-sm" style={{ borderColor: "#E9D8B4" }}
                     data-testid="scout-manual-name" />
              <input placeholder="Address (optional)" value={manualForm.address}
                     onChange={(e) => setManualForm({ ...manualForm, address: e.target.value })}
                     className="w-full rounded-lg border px-3 py-2 mb-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
              <input placeholder="Phone (optional)" value={manualForm.phone}
                     onChange={(e) => setManualForm({ ...manualForm, phone: e.target.value })}
                     className="w-full rounded-lg border px-3 py-2 mb-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
              <button
                disabled={!manualForm.name.trim()}
                onClick={() => setVendor({
                  store_name_raw: manualForm.name.trim(),
                  store_address_raw: manualForm.address.trim() || undefined,
                  store_phone_raw: manualForm.phone.trim() || undefined,
                  vendor_name: manualForm.name.trim(),
                  gps: coords,
                })}
                className="rounded-full px-4 py-2 text-white text-sm font-semibold disabled:opacity-40"
                style={{ background: GREEN }}
                data-testid="scout-manual-use"
              >
                Use this store
              </button>
              {vendor?.store_name_raw && (
                <div className="mt-2 text-xs" style={{ color: GREEN }}><Check className="w-3 h-3 inline" /> Using: {vendor.store_name_raw}</div>
              )}
            </div>
          )}
        </>
      )}

      <div className="mt-8 flex items-center justify-between">
        <Link to="/food-pulse/deals" className="text-sm opacity-70">← Cancel</Link>
        <button
          disabled={!vendor}
          onClick={onNext}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-white disabled:opacity-40"
          style={{ background: GREEN }}
          data-testid="scout-next-1"
        >
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Step 2 — Product identification (catalog search OR photo)
// ------------------------------------------------------------------
function ProductStep({ product, setProduct, photoUrl, setPhotoUrl, vendor, onBack, onNext }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // "Suggest new product" flow — surfaces when catalog has no match
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestName, setSuggestName] = useState("");
  const [suggestAltNames, setSuggestAltNames] = useState("");
  const [suggestDesc, setSuggestDesc] = useState("");
  const [suggestPhotos, setSuggestPhotos] = useState([]);   // uploaded URLs
  const [suggestSubmitted, setSuggestSubmitted] = useState(false);
  const suggestFileRef = useRef(null);

  useEffect(() => {
    if (q.length < 2) { setItems([]); return; }
    let cancel = false;
    axios.get(`${API}/api/food-items`, { params: { q, limit: 12 } })
      .then((r) => { if (!cancel) setItems(r.data.items || r.data || []); })
      .catch(() => !cancel && setItems([]));
    return () => { cancel = true; };
  }, [q]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) return alert(`Photo too large (max ${MAX_UPLOAD_BYTES / 1024 / 1024} MB)`);
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(rd.result); rd.onerror = rej;
        rd.readAsDataURL(f);
      });
      const r = await http.post(`/entity/upload`, { image: dataUrl });
      setPhotoUrl(r.data.url);
    } catch (err) {
      alert(err?.response?.data?.detail || "Upload failed. Try a smaller photo.");
    } finally { setUploading(false); }
  };

  const onSuggestPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) return alert(`Photo too large (max ${MAX_UPLOAD_BYTES / 1024 / 1024} MB)`);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(rd.result); rd.onerror = rej;
        rd.readAsDataURL(f);
      });
      const r = await http.post(`/entity/upload`, { image: dataUrl });
      setSuggestPhotos((prev) => [...prev, r.data.url]);
    } catch (err) {
      alert(err?.response?.data?.detail || "Upload failed.");
    }
  };

  const submitSuggestion = async () => {
    if (!suggestName.trim()) return alert("Please enter the product name.");
    try {
      await http.post(`/product-suggestions`, {
        product_name: suggestName.trim(),
        alt_names: suggestAltNames.trim() || undefined,
        product_description: suggestDesc.trim() || undefined,
        product_images: suggestPhotos,
        geo: {
          city_village: vendor?.city || undefined,
          state_province: vendor?.state || undefined,
        },
      });
      setSuggestSubmitted(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not submit suggestion.");
    }
  };

  return (
    <div data-testid="scout-step-product">
      <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: AMBER }}>Step 2 of 3</div>
      <h2 className="font-display text-3xl sm:text-4xl mb-2">What's on sale?</h2>
      <p className="opacity-70 text-sm mb-6">Search our food catalog first — you'll get nutrition, recipes, and reuse the item's photo. Or snap a fresh photo below.</p>

      <div className="flex items-center gap-2 rounded-full border px-4 py-2 mb-4" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
        <Search className="w-4 h-4 opacity-50" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bananas, sourdough, salmon…"
               className="flex-1 outline-none text-sm bg-transparent" data-testid="scout-product-search" />
      </div>

      {items.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-3 mb-4 max-h-64 overflow-y-auto">
          {items.map((it) => (
            <button key={it.slug}
                    onClick={() => setProduct({ slug: it.slug, name: it.name, image: it.hero_image || it.image_url, category: it.category })}
                    className={`text-left rounded-xl border p-2 flex gap-2 items-center hover:border-emerald-800 transition-all ${product?.slug === it.slug ? "ring-2 ring-emerald-800" : ""}`}
                    style={{ borderColor: product?.slug === it.slug ? GREEN : "#E9D8B4", background: "#fff" }}
                    data-testid={`scout-product-${it.slug}`}>
              <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0" style={{ background: "linear-gradient(135deg,#F6C87A,#FBF7EF)" }}>
                {(it.hero_image || it.image_url) && <img src={it.hero_image || it.image_url} className="w-full h-full object-cover" alt="" />}
              </div>
              <div className="text-sm min-w-0 flex-1">
                <div className="font-semibold truncate">{it.name}</div>
                {it.category && <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{it.category}</div>}
              </div>
            </button>
          ))}
        </div>
      )}

      {q.length >= 2 && items.length === 0 && !showSuggest && (
        <div className="rounded-2xl border p-3 mb-4 text-sm flex items-center justify-between" style={{ borderColor: "#E9D8B4", background: "#FFF7E4" }}>
          <span>No match for "<strong>{q}</strong>" in our catalog.</span>
          <button onClick={() => { setShowSuggest(true); setSuggestName(q); }}
                  className="rounded-full px-3 py-1.5 text-xs font-semibold text-white"
                  style={{ background: GREEN }}
                  data-testid="scout-suggest-new">
            + Suggest this item
          </button>
        </div>
      )}

      {product && (
        <div className="rounded-xl p-3 mb-4 flex items-center gap-3" style={{ background: "#F0F5F1" }}>
          <Check className="w-5 h-5" style={{ color: GREEN }} />
          <div className="flex-1"><strong>{product.name}</strong> selected from catalog</div>
          <button onClick={() => setProduct(null)} className="opacity-60"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Suggest new product panel */}
      {showSuggest && !suggestSubmitted && (
        <div className="rounded-2xl border-2 p-4 mb-4" style={{ borderColor: GREEN, background: "#fff" }} data-testid="scout-suggest-panel">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4" style={{ color: GREEN }} />
            <strong>Help us grow the food database</strong>
          </div>
          <p className="text-xs opacity-70 mb-3">
            Give us a name and a photo. Our AI will fill in nutrition, cooking methods, recipes, and photos overnight, then add it to the catalog for everyone.
          </p>
          <div className="grid gap-2">
            <input value={suggestName} onChange={(e) => setSuggestName(e.target.value)}
                   placeholder="Product name *"
                   className="rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }}
                   data-testid="scout-suggest-name" />
            <input value={suggestAltNames} onChange={(e) => setSuggestAltNames(e.target.value)}
                   placeholder="Alternate names (comma separated) — helps the AI"
                   className="rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
            <textarea rows={2} value={suggestDesc} onChange={(e) => setSuggestDesc(e.target.value)}
                      placeholder="Short description — where you saw it, unique traits, packaging…"
                      className="rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
            <div className="flex items-center gap-2 flex-wrap">
              {suggestPhotos.map((u, i) => (
                <div key={i} className="relative">
                  <img src={u} alt="" className="w-16 h-16 object-cover rounded-lg" />
                  <button onClick={() => setSuggestPhotos((p) => p.filter((_, j) => j !== i))}
                          className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 border shadow">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button onClick={() => suggestFileRef.current?.click()}
                      className="w-16 h-16 rounded-lg border-2 border-dashed flex items-center justify-center"
                      style={{ borderColor: AMBER, color: AMBER }}
                      data-testid="scout-suggest-photo-add">
                <Camera className="w-5 h-5" />
              </button>
              <input type="file" accept="image/*" capture="environment" ref={suggestFileRef} onChange={onSuggestPhoto} className="hidden" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button onClick={submitSuggestion}
                    className="rounded-full px-4 py-2 text-white text-sm font-semibold"
                    style={{ background: GREEN }}
                    data-testid="scout-suggest-submit">
              Submit for AI enrichment
            </button>
            <button onClick={() => setShowSuggest(false)} className="text-xs opacity-70 underline">Cancel</button>
          </div>
        </div>
      )}
      {suggestSubmitted && (
        <div className="rounded-2xl border p-3 mb-4 text-sm flex items-center gap-2" style={{ borderColor: GREEN, background: "#F0F5F1" }} data-testid="scout-suggest-thanks">
          <Check className="w-4 h-4" style={{ color: GREEN }} />
          Thanks — <strong>+5 points</strong> queued once the item is enriched and approved. You can still continue and publish this deal using a fresh photo below.
        </div>
      )}

      {/* Photo capture */}
      <div className="rounded-2xl border-2 border-dashed p-4" style={{ borderColor: AMBER, background: "#FFF7E4" }}>
        <div className="flex items-center gap-2 mb-2">
          <Camera className="w-5 h-5" style={{ color: AMBER }} />
          <strong>Add a photo (recommended)</strong>
        </div>
        <p className="text-xs opacity-70 mb-3">A real snapshot makes your deal 3× more likely to be trusted. Photos of the price tag work great.</p>
        {photoUrl ? (
          <div className="relative inline-block">
            <img src={photoUrl} alt="uploaded" className="w-40 h-40 object-cover rounded-xl" />
            <button onClick={() => setPhotoUrl(null)} className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow border"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="rounded-full px-4 py-2 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                  style={{ background: GREEN }}
                  data-testid="scout-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            {uploading ? "Uploading…" : "Take / choose photo"}
          </button>
        )}
        <input type="file" accept="image/*" capture="environment" ref={fileRef} onChange={onFile} className="hidden" data-testid="scout-upload-input" />
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button onClick={onBack} className="text-sm opacity-70 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
        <button
          disabled={!product && !photoUrl}
          onClick={onNext}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-white disabled:opacity-40"
          style={{ background: GREEN }}
          data-testid="scout-next-2"
        >
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Step 3 — Deal specifics
// ------------------------------------------------------------------
function DealStep({ vendor, product, photoUrl, onBack, onSubmit, submitting }) {
  const [title, setTitle] = useState(product?.name ? `${product.name} — on sale` : "");
  const [description, setDescription] = useState("");
  const [origPrice, setOrigPrice] = useState("");
  const [discount, setDiscount] = useState(30);
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState(product?.category === "fruits" || product?.category === "vegetables" ? "lb" : "each");
  const [hours, setHours] = useState(6);
  const [pickup, setPickup] = useState("");
  const [kind, setKind] = useState("surplus");

  const dealPrice = origPrice && discount ? (Number(origPrice) * (1 - Number(discount) / 100)).toFixed(2) : null;

  const submit = () => {
    onSubmit({
      ...vendor,
      food_item_slug: product?.slug,
      scout_photo_url: photoUrl,
      title: title.trim(),
      description: description.trim() || undefined,
      kind,
      original_price: origPrice ? Number(origPrice) : undefined,
      discount_pct: discount ? Number(discount) : undefined,
      quantity_available: qty ? Number(qty) : undefined,
      unit,
      expires_in_hours: Number(hours),
      pickup_location: pickup.trim() || undefined,
    });
  };

  return (
    <div data-testid="scout-step-deal">
      <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: AMBER }}>Step 3 of 3</div>
      <h2 className="font-display text-3xl sm:text-4xl mb-2">Deal details</h2>
      <p className="opacity-70 text-sm mb-6">Community deals must have an expiry so no one chases stale listings. Max 72 hours.</p>

      <div className="grid gap-4">
        <label className="text-sm">
          <span className="text-xs opacity-70 uppercase tracking-widest font-mono">Title *</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)}
                 className="mt-1 w-full rounded-xl border px-4 py-2" style={{ borderColor: "#E9D8B4" }}
                 data-testid="scout-deal-title" />
        </label>
        <label className="text-sm">
          <span className="text-xs opacity-70 uppercase tracking-widest font-mono">Notes (optional)</span>
          <div className="mt-1">
            <TranslatableInput
              as="textarea"
              rows={2}
              value={description}
              onChange={(next) => setDescription(next)}
              placeholder="e.g. Marked at $2, tag says −50%. Aisle 3 endcap."
              testid="scout-deal-notes"
            />
          </div>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="text-xs opacity-70 uppercase tracking-widest font-mono">Deal type</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)}
                    className="mt-1 w-full rounded-xl border px-4 py-2 bg-white" style={{ borderColor: "#E9D8B4" }}
                    data-testid="scout-deal-kind">
              <option value="surplus">🌍 Surplus (waste warrior +20 pts)</option>
              <option value="offer">🏷️ Special offer</option>
              <option value="coupon">🎟️ Coupon</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="text-xs opacity-70 uppercase tracking-widest font-mono">Expires in (h)</span>
            <input type="number" min={1} max={72} value={hours} onChange={(e) => setHours(e.target.value)}
                   className="mt-1 w-full rounded-xl border px-4 py-2" style={{ borderColor: "#E9D8B4" }}
                   data-testid="scout-deal-hours" />
          </label>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <label className="text-sm">
            <span className="text-xs opacity-70 uppercase tracking-widest font-mono">Original $</span>
            <input type="number" step="0.01" value={origPrice} onChange={(e) => setOrigPrice(e.target.value)}
                   className="mt-1 w-full rounded-xl border px-4 py-2" style={{ borderColor: "#E9D8B4" }}
                   data-testid="scout-deal-orig-price" />
          </label>
          <label className="text-sm">
            <span className="text-xs opacity-70 uppercase tracking-widest font-mono">Discount %</span>
            <input type="number" min={0} max={100} value={discount} onChange={(e) => setDiscount(e.target.value)}
                   className="mt-1 w-full rounded-xl border px-4 py-2" style={{ borderColor: "#E9D8B4" }}
                   data-testid="scout-deal-discount" />
          </label>
          <label className="text-sm">
            <span className="text-xs opacity-70 uppercase tracking-widest font-mono">Sale $ (auto)</span>
            <input value={dealPrice || ""} readOnly
                   className="mt-1 w-full rounded-xl border px-4 py-2 bg-gray-50" style={{ borderColor: "#E9D8B4" }} />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="text-xs opacity-70 uppercase tracking-widest font-mono">Quantity</span>
            <input type="number" min={0} value={qty} onChange={(e) => setQty(e.target.value)}
                   className="mt-1 w-full rounded-xl border px-4 py-2" style={{ borderColor: "#E9D8B4" }}
                   data-testid="scout-deal-qty" />
          </label>
          <label className="text-sm">
            <span className="text-xs opacity-70 uppercase tracking-widest font-mono">Unit</span>
            <input value={unit} onChange={(e) => setUnit(e.target.value)}
                   className="mt-1 w-full rounded-xl border px-4 py-2" style={{ borderColor: "#E9D8B4" }} />
          </label>
        </div>

        <label className="text-sm">
          <span className="text-xs opacity-70 uppercase tracking-widest font-mono">Pickup (optional)</span>
          <input value={pickup} onChange={(e) => setPickup(e.target.value)}
                 className="mt-1 w-full rounded-xl border px-4 py-2" style={{ borderColor: "#E9D8B4" }}
                 placeholder="e.g. Front counter, ask for manager" />
        </label>
      </div>

      <div className="mt-6 rounded-2xl p-4 flex items-start gap-3" style={{ background: "#F0F5F1" }}>
        <Award className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: GREEN }} />
        <div className="text-sm">
          You'll earn <strong>+{kind === "surplus" ? 30 : 10} points</strong> for this deal
          ({kind === "surplus" && <span>Waste Warrior bonus + </span>}base scout points).
          If the store claims it, you get <strong>+5 more</strong>.
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button onClick={onBack} className="text-sm opacity-70 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
        <button onClick={submit} disabled={!title.trim() || submitting}
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-white disabled:opacity-40"
                style={{ background: AMBER, color: GREEN }}
                data-testid="scout-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Publish deal
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Main SpotADeal page
// ------------------------------------------------------------------
export default function SpotADeal() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [vendor, setVendor] = useState(null);
  const [product, setProduct] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }}>
        <div className="max-w-md text-center">
          <Camera className="w-14 h-14 mx-auto mb-4" style={{ color: AMBER }} />
          <h1 className="font-display text-3xl mb-3">Spot a deal</h1>
          <p className="opacity-80 mb-6">Sign in to publish community deals and earn Food Scout points.</p>
          <Link to="/login" className="rounded-full px-6 py-3 font-semibold text-white" style={{ background: GREEN }} data-testid="scout-login-cta">Sign in</Link>
        </div>
      </div>
    );
  }

  const submit = async (payload) => {
    setSubmitting(true);
    try {
      const r = await http.post(`/deals/community`, payload);
      setResult(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Submit failed. Please check your inputs.");
    } finally { setSubmitting(false); }
  };

  // Success screen
  if (result) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }} data-testid="scout-success">
      <div className="max-w-md text-center bg-white rounded-3xl p-8 shadow-xl" style={{ border: "1px solid #E9D8B4" }}>
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: AMBER }}>
          <Award className="w-8 h-8" style={{ color: GREEN }} />
        </div>
        <h1 className="font-display text-3xl mb-2">Deal published!</h1>
        <div className="text-lg mb-4">You earned <strong>+{result.points_awarded} Food Scout points</strong></div>
        <div className="rounded-xl p-3 text-left mb-6" style={{ background: CREAM, border: "1px dashed #E9D8B4" }}>
          <div className="text-[11px] font-mono uppercase tracking-widest opacity-60">Your deal</div>
          <div className="font-semibold">{result.title}</div>
          <div className="text-xs opacity-70">at {result.vendor_name}</div>
          {result.claim_status === "pending_vendor" && (
            <div className="mt-2 text-[11px] rounded-full inline-block px-2 py-0.5" style={{ background: "#FFF7E4", color: "#8B5E10" }}>
              <ShieldCheck className="w-3 h-3 inline" /> Store not registered yet — they can claim later
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-center">
          <Link to={`/food-pulse/deals/${result.slug}`} className="rounded-full px-4 py-2 text-white text-sm font-semibold" style={{ background: GREEN }} data-testid="scout-view-deal">View deal</Link>
          <button onClick={() => { setResult(null); setStep(1); setVendor(null); setProduct(null); setPhotoUrl(null); }}
                  className="rounded-full px-4 py-2 text-sm font-semibold border-2" style={{ borderColor: GREEN, color: GREEN }}
                  data-testid="scout-post-another">
            Spot another
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="spot-a-deal">
      <SeoHead section="food-pulse-spot" title="Spot a deal — Food Pulse" description="Snap a photo of a deal you spotted at a local store. Earn Food Scout points." />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: GREEN, color: "#fff" }}>
        <div className="absolute inset-0"
             style={{ backgroundImage: `linear-gradient(115deg, ${GREEN} 0%, rgba(15,61,46,0.85) 60%, rgba(15,61,46,0.4) 100%), url('https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=2000&q=80')`, backgroundSize: "cover", backgroundPosition: "center" }}
             aria-hidden />
        <div className="relative max-w-3xl mx-auto px-4 py-10 sm:py-14">
          <div className="inline-flex items-center gap-2 rounded-full border-2 px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.28em] mb-4"
               style={{ borderColor: AMBER, color: "#F6C87A" }}>
            <Camera className="w-3.5 h-3.5" /> Food scout
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight">
            Spot a deal. <span style={{ color: AMBER }}>Feed the network.</span>
          </h1>
          <p className="mt-4 opacity-90 max-w-xl text-sm sm:text-base">
            See a great sale at a local store? Snap a photo and publish it in 60 seconds. Earn points every time someone uses it — and unlock rewards from vendors.
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex-1">
              <div className={`h-1 rounded-full`}
                   style={{ background: n <= step ? GREEN : "#E9D8B4" }} />
              <div className="mt-2 text-[10px] font-mono uppercase tracking-widest opacity-70">
                {n === 1 ? "Store" : n === 2 ? "Product" : "Deal"}
              </div>
            </div>
          ))}
        </div>

        {step === 1 && (
          <VendorStep vendor={vendor} setVendor={setVendor} onNext={() => setStep(2)} />
        )}
        {step === 2 && (
          <ProductStep product={product} setProduct={setProduct}
                       photoUrl={photoUrl} setPhotoUrl={setPhotoUrl}
                       vendor={vendor}
                       onBack={() => setStep(1)} onNext={() => setStep(3)} />
        )}
        {step === 3 && (
          <DealStep vendor={vendor} product={product} photoUrl={photoUrl}
                    onBack={() => setStep(2)} onSubmit={submit} submitting={submitting} />
        )}
      </div>
    </div>
  );
}
